/**
 * Quoted replies ("Quote reply", ENG-4137) for one view — the conversation or a comment thread.
 *
 * Both views need the identical pair: the quote the composer is holding, written by the
 * list's Reply action, and the jump a reply's quote card asks for. Holding them here keeps
 * the two views layout-only, the same reason `use-channel-message-interactions` exists.
 *
 * ## Scoped, not global
 *
 * The pending quote AND the quote jump are tagged with `scopeKey` — the cid for the
 * conversation, the cid plus the root for a thread. A view that swaps what it shows in
 * place (the office aside moving between orders, the inbox opening another thread) must
 * not post one place's quote into another (the server would refuse it anyway), nor keep
 * resolving the last place's jump — the resolver would page back through the new
 * conversation looking for a message it cannot contain.
 *
 * ## The jump
 *
 * A quote card's click becomes a message TARGET for the view's own resolver, so it pages
 * back like a deep link does. Its nonces count DOWN, so they never equal a surface's (which
 * count up) and leave the jump looking like a repeat. A target the surface asks for later
 * (a search result, a pin) wins over an earlier quote jump.
 */

import { useCallback, useEffect, useRef, useState } from "react";

import type {
  ChannelMessage,
  ChannelQuotedMessage,
} from "../_api/channel-endpoints";
import {
  type ChannelMessageTarget,
  messageTargetKey,
} from "../_libs/message-target";
import { toQuotedMessage } from "../_libs/quoted-message";

export function useQuotedReply({
  scopeKey,
  focusedMessage,
}: {
  /** What the pending quote belongs to — see "Scoped, not global" above. */
  scopeKey: string;
  /** The surface's own scroll target, which a later quote jump stands in for. */
  focusedMessage: ChannelMessageTarget | null | undefined;
}) {
  const [pending, setPending] = useState<{
    scopeKey: string;
    quote: ChannelQuotedMessage;
  } | null>(null);
  const quotedMessage = pending?.scopeKey === scopeKey ? pending.quote : null;

  const changeQuotedMessage = useCallback(
    (quote: ChannelQuotedMessage | null) =>
      setPending(quote ? { scopeKey, quote } : null),
    [scopeKey],
  );

  const replyToMessage = useCallback(
    (message: ChannelMessage) => changeQuotedMessage(toQuotedMessage(message)),
    [changeQuotedMessage],
  );

  const jumpCountRef = useRef(0);
  const [jump, setJump] = useState<{
    scopeKey: string;
    target: ChannelMessageTarget;
  } | null>(null);
  const quoteJump = jump?.scopeKey === scopeKey ? jump.target : null;
  const openQuotedMessage = useCallback(
    (messageId: string) => {
      jumpCountRef.current -= 1;
      setJump({
        scopeKey,
        target: { id: messageId, nonce: jumpCountRef.current },
      });
    },
    [scopeKey],
  );

  const surfaceTargetKey = messageTargetKey(focusedMessage);
  useEffect(() => {
    if (surfaceTargetKey) setJump(null);
  }, [surfaceTargetKey]);

  return {
    /** What the composer shows and the next post quotes. */
    quotedMessage,
    /** The composer's setter: `null` once spent or dismissed, the quote again on a failed send. */
    changeQuotedMessage,
    /** The list's Reply action. */
    replyToMessage,
    /** A quote card's click. */
    openQuotedMessage,
    /** The scroll target to resolve: the latest quote jump, else the surface's own. */
    target: quoteJump ?? focusedMessage ?? null,
  };
}
